/**
 * DosyaTurleriPage.jsx — Sistem Ayarları › Dosya Türleri (backlog #158, 2026-10-11)
 * Route: /Management/SistemAyarlari/DosyaTurleri  (yalnızca personel; uçlar da öyle)
 *
 * file_type kataloğu, ağaç olarak: Kişi belgeleri (1), Firma belgeleri (47), BetterStands fotoğraf
 * kategorileri (65), proje / rapor / tutanak türleri… Eski sayfa (Configurations/FileTypes) ölü bir Node
 * API'sine (gw.sakagrup.com/api/filetypes, 404) bağlıydı; bu sayfa Java'nın FileTypeController'ını kullanır.
 *   - Kodun id ile okuduğu türler (FileTypeConstant.KODDA_KULLANILAN) ve BetterStands kategorileri kilitli:
 *     yalnızca adları ve açıklaması değişir.
 *   - Diğerleri yalnızca hiçbir dosya, belge, fotoğraf, kural, taşıma geçmişi, belge izni ya da alt tür
 *     onlara bağlı değilse silinir. Sayfa `silinebilir` / `koddaKullanilan` bayraklarını sunucudan okur.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Card, CardBody, CardHeader, Table, Button, Badge, Alert, Spinner, Modal, ModalHeader, ModalBody,
  ModalFooter, Label, Input, Row, Col, FormGroup,
} from "reactstrap";
import { Plus, Pencil, Trash2, Lock, Image as ImageIcon, CalendarDays } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  getDosyaTurleri, createDosyaTuru, updateDosyaTuru, deleteDosyaTuru,
} from "../../../services/veriAyarlariService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const BOS_FORM = { name: "", adEn: "", extra: "", parentId: 0, isImage: false, activeFuarNeeded: false };

const KULLANIM_ETIKETLERI = [
  ["dosya", "dosya"],
  ["firmaBelgesi", "firma belgesi"],
  ["standFotografi", "stand fotoğrafı"],
  ["projeVeSalonDosyasi", "proje / salon dosyası"],
  ["kural", "kural"],
  ["tasimaGecmisi", "taşıma kaydı"],
  ["kisiBelgeIzni", "belge izni"],
  ["altTurSayisi", "alt tür"],
];

function kullanimOzeti(row) {
  const parcalar = KULLANIM_ETIKETLERI
    .filter(([k]) => Number(row[k]) > 0)
    // WAS: .map(([k, ad]) => `${Number(row[k]).toLocaleString("tr-TR")} ${ad}`);
    // #349: the labels above stay as the Turkish defaults; the summary translates them by key when it is built.
    .map(([k, ad]) => `${Number(row[k]).toLocaleString(currentLocaleTag())} ${i18n.t(`ayarlar:dosyaTurleri.kullanimEtiketleri.${k}`, { defaultValue: ad })}`);
  return parcalar.length ? parcalar.join(" · ") : i18n.t("ayarlar:dosyaTurleri.kullanilmiyor");
}

/** Ağacı derinlik-öncelikli sıraya dizer; {row, derinlik}. Yetim (üstü olmayan) türler sonda. */
function agacSirasi(turler) {
  const cocuklar = new Map();
  for (const t of turler) {
    const p = t.parentId || 0;
    if (!cocuklar.has(p)) cocuklar.set(p, []);
    cocuklar.get(p).push(t);
  }
  const out = [];
  const gorulen = new Set();
  const gez = (parentId, derinlik) => {
    for (const t of cocuklar.get(parentId) || []) {
      if (gorulen.has(t.fileTypeId)) continue;
      gorulen.add(t.fileTypeId);
      out.push({ row: t, derinlik });
      gez(t.fileTypeId, derinlik + 1);
    }
  };
  gez(0, 0);
  for (const t of turler) if (!gorulen.has(t.fileTypeId)) out.push({ row: t, derinlik: 0 });
  return out;
}

/** Bir türün kendisi ve tüm alt türleri: üst tür seçiminden çıkarılır (backend de reddeder). */
function altAgac(turler, kokId) {
  const out = new Set([kokId]);
  let degisti = true;
  while (degisti) {
    degisti = false;
    for (const t of turler) if (out.has(t.parentId) && !out.has(t.fileTypeId)) { out.add(t.fileTypeId); degisti = true; }
  }
  return out;
}

export default function DosyaTurleriPage() {
  const { t } = useTranslation("ayarlar");
  useEffect(() => { document.title = i18n.t("ayarlar:dosyaTurleri.dosyaTurleriFds"); }, []);

  const [turler, setTurler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [bilgi, setBilgi] = useState(null);
  const [yenile, setYenile] = useState(0);
  const [arama, setArama] = useState("");

  const [modal, setModal] = useState(null); // { duzenlenen: row | null }
  const [form, setForm] = useState(BOS_FORM);
  const [kaydediyor, setKaydediyor] = useState(false);
  const [modalHata, setModalHata] = useState(null);
  const [silinecek, setSilinecek] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const rows = await getDosyaTurleri();
        if (!ignore) setTurler(rows);
      } catch (e) {
        if (!ignore) setHata(e.message);
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [yenile]);

  const sirali = useMemo(() => agacSirasi(turler), [turler]);
  const gorunen = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase("tr-TR");
    if (!q) return sirali;
    return sirali.filter(({ row }) =>
      [row.fileTypeName, row.adEn, row.parentName, String(row.fileTypeId)]
        .some((v) => v && String(v).toLocaleLowerCase("tr-TR").includes(q)));
  }, [sirali, arama]);
  const ustSecenekleri = useMemo(() => {
    const disarida = modal?.duzenlenen ? altAgac(turler, modal.duzenlenen.fileTypeId) : new Set();
    return sirali.filter(({ row }) => !disarida.has(row.fileTypeId));
  }, [sirali, turler, modal]);

  function ac(duzenlenen) {
    setModalHata(null);
    setForm(duzenlenen ? {
      name: duzenlenen.fileTypeName ?? "",
      adEn: duzenlenen.adEn ?? "",
      extra: duzenlenen.extra ?? "",
      parentId: duzenlenen.parentId ?? 0,
      isImage: Number(duzenlenen.isImage) === 1,
      activeFuarNeeded: Number(duzenlenen.activeFuarNeeded) === 1,
    } : BOS_FORM);
    setModal({ duzenlenen });
  }

  async function kaydet() {
    if (!form.name.trim()) { setModalHata(t("dosyaTurleri.adGerekli")); return; }
    setKaydediyor(true);
    setModalHata(null);
    try {
      if (modal.duzenlenen) await updateDosyaTuru(modal.duzenlenen.fileTypeId, form);
      else await createDosyaTuru(form);
      setBilgi(modal.duzenlenen ? t("dosyaTurleri.dosyaTuruGuncellendi") : t("dosyaTurleri.dosyaTuruEklendi"));
      setModal(null);
      setYenile((n) => n + 1);
    } catch (e) {
      setModalHata(e.message);
    } finally {
      setKaydediyor(false);
    }
  }

  async function sil() {
    setKaydediyor(true);
    setModalHata(null);
    try {
      await deleteDosyaTuru(silinecek.fileTypeId);
      setBilgi(t("dosyaTurleri.silindi", { fileTypeName: silinecek.fileTypeName }));
      setSilinecek(null);
      setYenile((n) => n + 1);
    } catch (e) {
      setModalHata(e.message);
    } finally {
      setKaydediyor(false);
    }
  }

  const kilitli = modal?.duzenlenen?.koddaKullanilan === true;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("dosyaTurleri.dosyaTurleri")} pageTitle={t("dosyaTurleri.sistemAyarlari")} />
        {hata && <Alert color="danger">{hata}</Alert>}
        {bilgi && <Alert color="success" toggle={() => setBilgi(null)}>{bilgi}</Alert>}
        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h5 className="mb-0">{t("dosyaTurleri.dosyaTurleri")}</h5>
              <small className="text-muted">
                {t("dosyaTurleri.belgeFotografVeProjeDosyalarinin")}
              </small>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Input bsSize="sm" placeholder={t("dosyaTurleri.araAdUstTurNumara")} value={arama}
                     onChange={(e) => setArama(e.target.value)} style={{ maxWidth: 260 }} />
              <Button color="primary" size="sm" onClick={() => ac(null)} disabled={yukleniyor}>
                <Plus size={14} className="me-1" />{t("dosyaTurleri.yeniDosyaTuru")}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {yukleniyor ? <Spinner size="sm" /> : (
              <div className="table-responsive">
                <Table size="sm" hover className="align-middle mb-0">
                  <thead>
                    <tr><th>#</th><th>{t("dosyaTurleri.ad")}</th><th>{t("dosyaTurleri.ingilizceAd")}</th><th>{t("dosyaTurleri.kullanim")}</th><th /></tr>
                  </thead>
                  <tbody>
                    {gorunen.map(({ row, derinlik }) => (
                      <tr key={row.fileTypeId}>
                        <td className="text-muted">{row.fileTypeId}</td>
                        <td style={{ paddingLeft: `${0.5 + derinlik * 1.25}rem` }}>
                          <span className={derinlik === 0 ? "fw-semibold" : undefined}>{row.fileTypeName}</span>
                          {row.koddaKullanilan && <Badge color="secondary" className="ms-2" title={t("dosyaTurleri.kodYaDaBetterStandsBu")}><Lock size={10} />{" "}{t("dosyaTurleri.kilitli")}</Badge>}
                          {Number(row.isImage) === 1 && <ImageIcon size={12} className="ms-2 text-muted" title={t("dosyaTurleri.gorsel")} />}
                          {Number(row.activeFuarNeeded) === 1 && <CalendarDays size={12} className="ms-1 text-muted" title={t("dosyaTurleri.fuaraOzel")} />}
                          {row.extra && <div className="small text-muted">{row.extra}</div>}
                        </td>
                        <td>{row.adEn || <span className="text-muted">—</span>}</td>
                        <td className="small text-muted">{kullanimOzeti(row)}</td>
                        <td className="text-end text-nowrap">
                          <Button size="sm" color="light" className="me-1" onClick={() => ac(row)} title={t("common:duzenle")}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="sm" color="light" disabled={!row.silinebilir}
                                  title={row.silinebilir ? t("common:sil") : row.koddaKullanilan ? t("dosyaTurleri.kilitliSilinemez") : t("dosyaTurleri.kullanimdaSilinemez")}
                                  onClick={() => { setModalHata(null); setSilinecek(row); }}>
                            <Trash2 size={14} className={row.silinebilir ? "text-danger" : "text-muted"} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      <Modal isOpen={!!modal} toggle={() => !kaydediyor && setModal(null)} size="lg">
        <ModalHeader toggle={() => !kaydediyor && setModal(null)}>
          {modal?.duzenlenen ? t("dosyaTurleri.dosyaTuru", { fileTypeId: modal.duzenlenen.fileTypeId }) : t("dosyaTurleri.yeniDosyaTuru")}
        </ModalHeader>
        <ModalBody>
          {modalHata && <Alert color="danger" className="small py-2">{modalHata}</Alert>}
          <Row className="g-2">
            <Col md={6}>
              <Label className="small mb-1" for="dt-ad">{t("dosyaTurleri.adTurkce")}</Label>
              <Input id="dt-ad" bsSize="sm" value={form.name} maxLength={150} disabled={kaydediyor}
                     onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </Col>
            <Col md={6}>
              <Label className="small mb-1" for="dt-en">{t("dosyaTurleri.ingilizceAd")}</Label>
              <Input id="dt-en" bsSize="sm" value={form.adEn} maxLength={150} disabled={kaydediyor}
                     onChange={(e) => setForm((f) => ({ ...f, adEn: e.target.value }))} />
            </Col>
            <Col md={12}>
              <Label className="small mb-1" for="dt-extra">{t("dosyaTurleri.aciklama")}</Label>
              <Input id="dt-extra" bsSize="sm" value={form.extra} maxLength={250} disabled={kaydediyor}
                     onChange={(e) => setForm((f) => ({ ...f, extra: e.target.value }))} />
            </Col>
            <Col md={6}>
              <Label className="small mb-1" for="dt-parent">{t("dosyaTurleri.ustTur")}</Label>
              <Input id="dt-parent" type="select" bsSize="sm" value={form.parentId} disabled={kaydediyor || kilitli}
                     onChange={(e) => setForm((f) => ({ ...f, parentId: Number(e.target.value) }))}>
                <option value={0}>{t("dosyaTurleri.anaTur")}</option>
                {ustSecenekleri.map(({ row, derinlik }) => (
                  <option key={row.fileTypeId} value={row.fileTypeId}>
                    {"  ".repeat(derinlik)}{row.fileTypeId} · {row.fileTypeName}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={6} className="d-flex flex-column justify-content-end">
              <FormGroup check className="mb-1">
                <Input id="dt-img" type="checkbox" checked={form.isImage} disabled={kaydediyor || kilitli}
                       onChange={(e) => setForm((f) => ({ ...f, isImage: e.target.checked }))} />
                <Label check className="small" for="dt-img">{t("dosyaTurleri.gorselFotografYuklenir")}</Label>
              </FormGroup>
              <FormGroup check className="mb-0">
                <Input id="dt-fuar" type="checkbox" checked={form.activeFuarNeeded} disabled={kaydediyor || kilitli}
                       onChange={(e) => setForm((f) => ({ ...f, activeFuarNeeded: e.target.checked }))} />
                <Label check className="small" for="dt-fuar">{t("dosyaTurleri.fuaraOzelHerFuarIcin")}</Label>
              </FormGroup>
            </Col>
          </Row>
          {kilitli && (
            <Alert color="secondary" className="small py-2 mt-3 mb-0 d-flex gap-2">
              <Lock size={14} className="mt-1 flex-shrink-0" />
              {t("dosyaTurleri.buTuruKodYaDa")}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" disabled={kaydediyor} onClick={() => setModal(null)}>{t("dosyaTurleri.vazgec")}</Button>
          <Button color="primary" size="sm" disabled={kaydediyor} onClick={kaydet}>
            {kaydediyor ? <Spinner size="sm" /> : t("common:kaydet")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!silinecek} toggle={() => !kaydediyor && setSilinecek(null)}>
        <ModalHeader toggle={() => !kaydediyor && setSilinecek(null)}>{t("dosyaTurleri.dosyaTurunuSil")}</ModalHeader>
        <ModalBody>
          {modalHata && <Alert color="danger" className="small py-2">{modalHata}</Alert>}
          <p className="mb-0">
            <strong>{silinecek?.fileTypeName}</strong>{" "}{t("dosyaTurleri.kaliciOlarakSilinecekHicbirKayit", { fileTypeId: silinecek?.fileTypeId })}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" disabled={kaydediyor} onClick={() => setSilinecek(null)}>{t("dosyaTurleri.vazgec")}</Button>
          <Button color="danger" size="sm" disabled={kaydediyor} onClick={sil}>
            {kaydediyor ? <Spinner size="sm" /> : t("common:sil")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
